import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DialogChatAPI from "../../../api/dialogChatApi";
import {
  createDialogChatAttachmentResourceCache,
  DialogChatAttachmentResource,
  DialogChatAttachmentSnapshot,
} from "../../../features/dialogChat/dialogChatAttachmentResources";
import { DialogChatFile } from "../../../types/DialogChat";
import { GlobalChatMessage } from "./globalChatCache";

const hasServerAttachmentReference = (message: GlobalChatMessage) =>
  !message.isLocalOnly &&
  message.files.length > 0 &&
  !message.realTimeBooking &&
  message.user !== "System";

export const useGlobalChatAttachmentResources = (
  token: string | null,
  messages: GlobalChatMessage[],
) => {
  const cacheRef = useRef(createDialogChatAttachmentResourceCache(DialogChatAPI));
  const [, forceRender] = useState(0);
  const messageIdsWithAttachments = useMemo(
    () =>
      messages
        .filter(hasServerAttachmentReference)
        .map((message) => message.messageId),
    [messages],
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    let isMounted = true;

    messageIdsWithAttachments.forEach((messageId) => {
      const snapshot = cacheRef.current.getSnapshot(messageId);
      if (snapshot.status === "idle") {
        cacheRef.current.load(token, messageId).then(() => {
          if (isMounted) {
            forceRender((version) => version + 1);
          }
        });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [messageIdsWithAttachments, token]);

  useEffect(() => {
    return () => {
      cacheRef.current.clear();
    };
  }, []);

  const retryAttachmentResources = useCallback(
    (messageId: string) => {
      if (!token) {
        return;
      }

      cacheRef.current.retry(token, messageId).then(() => {
        forceRender((version) => version + 1);
      });
      forceRender((version) => version + 1);
    },
    [token],
  );

  const getAttachmentSnapshot = useCallback(
    (message: GlobalChatMessage): DialogChatAttachmentSnapshot => {
      if (!hasServerAttachmentReference(message)) {
        const localResources = (message.files as DialogChatFile[]).map(
          (file, index) => ({
            id: file._id || `${message.messageId}-local-file-${index}`,
            messageId: message.messageId,
            kind: file.type?.startsWith("image") ? "image" : "file",
            status: "ready",
            type: file.type || "file",
            source: file.link || "",
            filename: file.filename,
            size:
              typeof file.size === "string" ? file.size : `${file.size || ""}`,
            canDownload: Boolean(file.link),
          }),
        ) as DialogChatAttachmentResource[];

        return {
          messageId: message.messageId,
          status: "ready",
          resources: localResources,
        };
      }

      return cacheRef.current.getSnapshot(message.messageId);
    },
    [],
  );

  return {
    getAttachmentSnapshot,
    retryAttachmentResources,
  };
};
